import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import moment from 'moment';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import {
    debounceTime,
    filter,
    map,
    startWith,
    take,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import * as fromDataServices from '../../../data-services';
import * as fromLookups from '../../../lookups/store';
import * as fromShared from '../../../shared';
import { LoggingService } from '../../../shared/services/logging';
import * as fromUser from '../../../user-info';
import * as fromUserInfo from '../../../user-info';
import * as fromStore from '../../store';

@Component({
    selector: 'ef-indirect-rate-management-container',
    templateUrl: './indirect-rate-management-container.component.html',
    styleUrls: ['./indirect-rate-management-container.component.scss'],
    standalone: false
})
export class IndirectRateManagementContainerComponent
    implements OnInit, OnDestroy
{
    IndirectRateManagementModel$: Observable<
        fromStore.Structure.IndirectRateStructure[]
    >;
    process$: Observable<fromShared.Process>;
    selectedError$: Observable<fromShared.Errors.IShowErrors>;
    fiscalYears$: Observable<fromShared.SelectItem[]>;
    currentFY$: Observable<string>; // Date formatted to MM/DD/YYYY
    userInfo$: Observable<fromUserInfo.IUserInfo>;

    selectedErrorWithStatus$: Observable<fromShared.IStatusMessage>;
    ShowErrorSubject = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    FiscalYearSelectedSubject = new ReplaySubject<string>(1);
    CancelSubject = new ReplaySubject(1);
    private active = true;
    showTimer$: Observable<boolean>;
    processingText = '';
    jobTimerDataService: EntityCollectionService<fromDataServices.JobTimer.JobTimerModel>;
    private jobTimerName = 'ef_indirectratesadjust_apply';

    constructor(
        public entityServices: EntityServices,
        private log: LoggingService,
        public store: Store<fromStore.Structure.State>
    ) {
        this.log.verbose({
            type: 'IndirectRateManagementContainerComponent::'
        });
        this.store.dispatch(fromStore.Structure.clearIndirectRateStructures());
        this.store.dispatch(fromStore.Structure.getIndirectRateStructures({}));

        this.jobTimerDataService =
            entityServices.getEntityCollectionService<fromDataServices.JobTimer.JobTimerModel>(
                fromDataServices.JobTimer.entityName
            );
    }
    ngOnInit(): void {
        this.setupObservables();
        this.setupSubscriptions();
        this.store.dispatch(fromStore.Structure.getIndirectRateTypes());

        this.jobTimerDataService.getWithQuery({
            Jobdesc: sessionStorage.getItem('jobTimerName')//this.jobTimerName
        });
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    setupSubscriptions() {
        this.showTimer$
            .pipe(
                takeWhile(() => this.active),
                filter(x => !!x),
                debounceTime(5000)
            )
            .subscribe(() => {
                // if (sessionStorage.getItem('jobTimerName') != 'ef_indirectratesadjust_apply') {
                //     this.processingText = 'Committing Rate Adjustment';
                // } else {
                //     this.processingText = 'Processing Rate Adjustment';
                // }
                this.jobTimerDataService.getWithQuery({
                    Jobdesc: sessionStorage.getItem('jobTimerName')//this.jobTimerName
                });
            });
        combineLatest([this.process$, this.ShowErrorSubject])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
        this.FiscalYearSelectedSubject.pipe(
            takeWhile(() => this.active),
            tap(fiscalYear =>
                this.log.verbose({
                    type: 'IndirectRateManagementContainerComponent:onFiscalYearSelected',
                    fiscalYear
                })
            )
        ).subscribe(fiscalYear => {
            const param = fiscalYear ? { FiscalYear: fiscalYear } : {};
            this.store.dispatch(
                fromStore.Structure.getIndirectRateStructures(param)
            );
        });
        this.CancelSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(
                this.FiscalYearSelectedSubject.pipe(
                    startWith(null as unknown as string)
                )
            )
        ).subscribe(([, fiscalYear]) => {
            this.store.dispatch(
                fromStore.Structure.clearIndirectRateStructures()
            );
            this.store.dispatch(
                fromStore.Structure.getIndirectRateStructures({
                    FiscalYear: fiscalYear
                })
            );
        });
    }

    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.userInfo$ = this.store.pipe(select(fromUserInfo.getUserInfo));

        this.IndirectRateManagementModel$ = this.store.pipe(
            select(fromStore.Structure.selectAll)
        );
        this.showTimer$ = this.jobTimerDataService.entities$.pipe(
            map(items =>
                items.find(item => item.Jobdesc === sessionStorage.getItem('jobTimerName'))//this.jobTimerName)
            ),
            map(item => (item ? item : { Timer: false })),
            map(item => item.Timer)
        );
        this.selectedError$ = this.store.pipe(
            select(fromShared.Errors.getShowErrors)
        );
        const errors$ = this.store.pipe(select(fromShared.Errors.getErrors));
        this.selectedErrorWithStatus$ = combineLatest([
            this.selectedError$,
            errors$
        ]).pipe(
            map(
                ([selected, errors]) =>
                    errors.filter(
                        e =>
                            e.MsgEntityID === selected.MsgEntityID &&
                            e.MsgProcessID === selected.MsgProcessID
                    )[0]
            )
        );
        this.fiscalYears$ = this.store.pipe(
            select(
                fromLookups.ScheduledDateSelectors.selectScheduledDateType({
                    ScheduledDateType: 'FYE'
                })
            ),
            filter(dates => !!dates && dates.length > 0),
            take(1),
            map(items =>
                items.map(item => ({
                    label: moment(item.ScheduledEndDate).format('L'),
                    value: moment(item.ScheduledEndDate).format('L')
                }))
            )
        );
        this.currentFY$ = this.store.pipe(
            select(fromUser.getUserInfo),
            filter(x => !!x),
            take(1),
            map(ui => moment(ui.FYEndDate).format('L'))
        );
    }
    onShowError(event) {
        this.ShowErrorSubject.next(event);
    }

    onSetProcessingText($event) {
        this.processingText = $event;
    }
}
